'use client';

import React, { useState, useMemo, useEffect } from 'react';
import LabeledTextInput from 'components/consumer/LabeledTextInput';
import ValidationError from 'components/consumer/ValidationError';
import useAdvancedSearchContext from '../../hooks/useAdvancedSearchContext';

const MIN_ALLOWED = 1;
const MAX_ALLOWED = 300;

const WidthRangeFilter = ({ showTitle = false }) => {
  const { minWidth, maxWidth, handleWidthChange } = useAdvancedSearchContext();

  const [minimum, setMinimum] = useState(minWidth ?? '');
  const [maximum, setMaximum] = useState(maxWidth ?? '');

  useEffect(() => {
    setMinimum(minWidth ?? '');
  }, [minWidth]);

  useEffect(() => {
    setMaximum(maxWidth ?? '');
  }, [maxWidth]);

  const minNum = minimum ? parseInt(minimum, 10) : NaN;
  const maxNum = maximum ? parseInt(maximum, 10) : NaN;

  const minError = useMemo(() => {
    if (!minimum) {
      return '';
    }

    if (Number.isNaN(minNum)) {
      return 'Please enter a valid number';
    }

    if (minNum < MIN_ALLOWED) {
      return `Minimum value cannot be less than ${MIN_ALLOWED}`;
    }

    if (minNum > MAX_ALLOWED) {
      return `Minimum value cannot be more than ${MAX_ALLOWED}`;
    }

    if (!Number.isNaN(maxNum) && minNum > maxNum) {
      return `Minimum value cannot be greater than ${maxNum}`;
    }

    return '';
  }, [minNum, maxNum, minimum]);

  const maxError = useMemo(() => {
    if (!maximum) {
      return '';
    }

    if (Number.isNaN(maxNum)) {
      return 'Please enter a valid number';
    }

    if (maxNum < MIN_ALLOWED) {
      return `Maximum value cannot be less than ${MIN_ALLOWED}`;
    }

    if (maxNum > MAX_ALLOWED) {
      return `Maximum value cannot be more than ${MAX_ALLOWED}`;
    }

    if (!Number.isNaN(minNum) && maxNum < minNum) {
      return `Maximum value cannot be less than ${minNum}`;
    }

    return '';
  }, [minNum, maxNum, maximum]);

  // Commit to the query only on a deliberate, valid event (blur / Enter) rather
  // than on every keystroke, so that in-range prefixes of an out-of-range value
  // (e.g. "5", "50" while typing "5000") never re-run the search. If the current
  // value is invalid we keep the previously committed query and leave the error
  // visible.
  const handleCommit = () => {
    if (!minError && !maxError) {
      handleWidthChange?.(minimum, maximum);
    }
  };

  const handleKeyDown = event => {
    if (event?.key === 'Enter') {
      handleCommit();
    }
  };

  const widthRangeError = useMemo(
    () => ({
      min: minError,
      max: maxError,
    }),
    [minError, maxError]
  );

  return (
    <div className="flex flex-col">
      {showTitle && <h3 className="text-base font-bold mb-2">Width</h3>}
      <div className="flex flex-row items-center flex-nowrap md:flex-wrap xl:flex-nowrap justify-start xl:justify-between gap-3 xl:gap-0">
        <LabeledTextInput
          variant="range-filter"
          type="number"
          name="min"
          data-xc="min-width-filter-field"
          label="Min"
          placeholder="Min"
          id="min-width"
          className="mr-0 xl:mr-[5px]"
          onChange={event => {
            setMinimum(event?.target?.value);
          }}
          onBlur={handleCommit}
          onKeyDown={handleKeyDown}
          value={minimum}
          postfixText="in."
          noMargin
        />

        <LabeledTextInput
          variant="range-filter"
          type="number"
          name="max"
          data-xc="max-width-filter-field"
          label="Max"
          placeholder="Max"
          id="max-width"
          className="ml-0 xl:ml-[5px]"
          onChange={event => {
            setMaximum(event?.target?.value);
          }}
          onBlur={handleCommit}
          onKeyDown={handleKeyDown}
          value={maximum}
          postfixText="in."
          noMargin
        />
      </div>

      {(widthRangeError.max || widthRangeError.min) && (
        <ValidationError block>
          {widthRangeError.min || widthRangeError.max}
        </ValidationError>
      )}
    </div>
  );
};

export default WidthRangeFilter;
